import React from 'react';
import { Helmet } from 'react-helmet-async';

import decodeHtmlEntities from '@fogia/core-web/utils/decodeHTMLEntities';
import type { ReferencesMap } from '@fogia/core-web/utils/internalRefToSlug';

import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { buildOgImageUrl } from '@fogia/core-web/libs/sanity/imageUrl';

import formatMetaDescription from '@fogia/core-web/components/metadata/utils/formatMetaDescription';

// @TODO: This should be moved to config when the structure is set
const NO_INDEX_PAGE_TEMPLATES: string[] = ['search_page'];

const ensureString = (value: unknown): string => (typeof value === 'string' ? value : '');

// @TODO: This should be moved to config when the structure is set
const NO_FOLLOW_PAGE_TEMPLATES: string[] = [];

interface PageSeoOpenGraph {
    title?: string;
    description?: string;
    /** Sanity image object (asset + optional crop/hotspot). OG URL built via buildOgImageUrl. */
    image?: { asset?: { _ref?: string; ref?: string }; crop?: unknown; hotspot?: unknown };
}

interface PageSeo {
    title?: string;
    description?: string;
    openGraph?: PageSeoOpenGraph;
}

interface PageDataFromState {
    title?: string;
    template?: string;
    settings?: { title?: string };
    seo?: PageSeo;
    references?: ReferencesMap;
}

const PageMeta = () => {
    const applicationDomain = useAppSelector(({ application }) => application?.site?.domain ?? '');
    const siteProtocol = useAppSelector(({ application }) => application?.site?.protocol || '');

    // Page data
    const pageData = useAppSelector(({ page }) => page?.data) as PageDataFromState | undefined;
    const resolvedSlug = useAppSelector(({ page }) => page?.resolvedSlug ?? '');

    const pageTemplate = pageData?.template ?? '';

    const pageSeo = pageData?.seo;
    const pageSeoTitle = pageSeo?.title ?? '';
    const pageTitle = pageData?.settings?.title ?? '';
    const pageSeoDescription = pageSeo?.description ?? '';

    const pageSeoOpenGraphData = (pageSeo?.openGraph ?? {}) as PageSeoOpenGraph;
    // noIndex/noFollow from Sanity not in use yet – uncomment when seo schema has these fields
    // const pageNoIndex = pageSeo?.noIndex === '1';
    // const pageNoFollow = pageSeo?.noFollow === '1';

    const ogTitle = pageSeoOpenGraphData?.title;
    const ogDescription = pageSeoOpenGraphData?.description;
    const pageSeoOpenGraphImage = buildOgImageUrl(pageSeoOpenGraphData?.image) ?? '';
    const canonical = resolvedSlug && applicationDomain ? `${siteProtocol}${applicationDomain}${resolvedSlug}` : '';
    const noIndex = NO_INDEX_PAGE_TEMPLATES.includes(pageTemplate) ? 'noindex' : '';
    const noFollow = ''; // NO_FOLLOW_PAGE_TEMPLATES + pageSeo.noFollow commented out until schema has it
    const robots = noIndex ? (noFollow ? `${noIndex}, ${noFollow}` : noIndex) : noFollow || undefined;
    const title = decodeHtmlEntities(String(pageSeoTitle || pageTitle || '')) ?? '';
    const description = formatMetaDescription(String(pageSeoDescription ?? '')) as string;
    const ogTitleStr = String(ogTitle ?? title ?? '');
    const ogDescStr = String(ogDescription ?? description ?? '');
    const safeOgTitle = ensureString(ogTitleStr);
    const safeOgDesc = ensureString(ogDescStr);
    const ogTitleFinal = ogTitleStr ? decodeHtmlEntities(safeOgTitle) : '';
    const ogDescriptionFinal = ogDescStr ? formatMetaDescription(safeOgDesc) : '';

    return (
        <Helmet>
            {/* General meta data */}
            {title && <title>{title}</title>}
            {canonical && <link rel="canonical" href={canonical} />}
            {description && <meta name="description" content={description} />}
            {robots && <meta name="robots" content={robots} />}

            {/* OpenGraph data */}
            {ogTitleFinal && <meta property="og:title" content={ogTitleFinal} />}
            {ogDescriptionFinal && <meta property="og:description" content={ogDescriptionFinal} />}
            {pageSeoOpenGraphImage && <meta property="og:image" content={pageSeoOpenGraphImage} />}

            {/* Twitter Card (same values as Open Graph) */}
            {ogTitleFinal && <meta name="twitter:title" content={ogTitleFinal} />}
            {ogDescriptionFinal && <meta name="twitter:description" content={ogDescriptionFinal} />}
            {pageSeoOpenGraphImage && <meta name="twitter:image" content={pageSeoOpenGraphImage} />}
        </Helmet>
    );
};

export default PageMeta;
